import React, { useEffect, useState } from 'react';
import { Check, X, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';

interface CreditBundlesProps {
  onClose: () => void;
  onSelectBundle: (bundle: string) => void;
}

interface Bundle {
  id: string;
  name: string;
  description: string;
  unit_amount: number;
  credits: number;
  metadata: {
    is_popular: boolean;
    sort_order: string;
    purchase_type: string;
  };
}

export function CreditBundles({ onClose, onSelectBundle }: CreditBundlesProps) {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBundles() {
      try {
        const { data, error } = await supabase.rpc('get_credit_bundles');
        if (error) throw error;
        setBundles(data || []);
      } catch (err) {
        console.error('Error fetching credit bundles:', err);
        setError('Failed to load credit bundles. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    fetchBundles();
  }, []);

  if (loading) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Purchase Card Credits"
        maxWidth="4xl"
      >
        <div className="p-4 sm:p-6">
          <div className="text-center py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border rounded-lg p-6">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Purchase Card Credits"
        maxWidth="4xl"
      >
        <div className="p-4 sm:p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Purchase Card Credits"
      maxWidth="4xl"
    >
      <div className="p-4 sm:p-6">
        <div className="text-center mb-8">
          <h3 className="text-lg sm:text-xl font-medium text-brand-secondary mb-2">
            Choose a credit bundle that works for you
          </h3>
          <p className="text-sm text-gray-600">
            Credits never expire and can be used for any card type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {bundles.map((bundle) => (
            <div
              key={bundle.id}
              className="border rounded-lg p-6 flex flex-col hover:shadow-lg transition-shadow relative"
            >
              {bundle.metadata.is_popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-brand-primary text-white px-4 py-1 rounded-full text-sm">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h4 className="text-xl font-medium text-brand-secondary mb-2">
                  {bundle.name}
                </h4>
                <div className="text-3xl font-bold text-brand-primary">
                  ${bundle.unit_amount.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {bundle.credits} credits
                </div>
              </div>

              <div className="flex-grow">
                <ul className="space-y-3 mb-6">
                  {bundle.description.split('\n').map((feature, index) => (
                    <li key={index} className="flex items-center text-sm">
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => onSelectBundle(bundle.id)}
                className="w-full px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                Select {bundle.name}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact our support team for assistance.
          </p>
        </div>
      </div>
    </Modal>
  );
} 